import { API_ENDPOINT, fetchWithAuth, getAuthToken, parseApiResponse } from "./index";

export interface AdminEventSummary {
  id: number;
  title: string;
  slug: string;
  status: string;
  startDate: string;
}

export type EventAdminPermissionLevel = "registrations" | "full";

export interface MyAdminEvent {
  event: AdminEventSummary;
  grantedAt: string | null;
  isSuperAdmin: boolean;
  permissionLevel: EventAdminPermissionLevel;
}

export interface EventAdminDirectoryUser {
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  assignments: Array<{
    event: AdminEventSummary;
    permissionLevel: EventAdminPermissionLevel;
    grantedAt: string;
  }>;
}

export interface AdminUserSearchResult {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

export type AdminAccessRequestType = "event_admin" | "post_events";

export interface AdminAccessRequest {
  id: number;
  status: string;
  requestType: AdminAccessRequestType;
  message: string | null;
  createdAt: string;
  reviewedAt: string | null;
  reviewNote: string | null;
  event?: AdminEventSummary | null;
  user?: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
}

export interface MyAdminEventsResult {
  events: MyAdminEvent[];
  canPostEvents: boolean;
}

export interface EventDashboard {
  event: AdminEventSummary & {
    endDate: string | null;
    registrationClosesAt: string | null;
    onsiteRegistrationOpen: boolean;
  };
  registrations: {
    confirmed: number;
    pendingPayment: number;
    cancelled: number;
  };
  revenue: {
    registrationPaidPence: number;
    orderPaidPence: number;
    paidOrderCount: number;
  };
  refunds: { processed: number };
  recentRegistrations: {
    registrationId: string;
    email: string;
    firstName: string;
    lastName: string;
    itemOrdered: string;
    amountPaidPence: number;
    currency: string;
    registrationDate: string | null;
    status: string;
  }[];
}

export interface EventRegistrationRow {
  registrationId: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  registrationType: string;
  paperId: string | null;
  paperTitle: string | null;
  itemOrdered: string;
  banquetQuantity: number;
  amountPaidPence: number;
  currency: string;
  status: string;
  registrationDate: string | null;
  participationFormat: string;
}

export interface EventAdminUser {
  id: number;
  user: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  permissionLevel: EventAdminPermissionLevel;
  grantedAt: string;
  grantedBy: { id: number; email: string };
}

export interface RefundRequestRow {
  id: number;
  status: string;
  reason: string;
  amountPence: number;
  requesterRole: string;
  createdAt: string;
  reviewedAt: string | null;
  reviewNote: string | null;
  order: {
    orderNumber: string | null;
    customerEmail: string;
    customerName: string | null;
    totalAmountPence: number;
    currency: string;
    status: string;
    registration?: {
      registrationId: string;
      firstName: string;
      lastName: string;
      itemOrdered: string;
    } | null;
  };
  requestedBy: {
    id: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
}

export async function getMyAdminEvents(): Promise<MyAdminEventsResult> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/admin/my-events`);
  return parseApiResponse<MyAdminEventsResult>(response);
}

export async function createPostEventsAccessRequest(
  message?: string,
): Promise<AdminAccessRequest> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/access-requests/post-events`,
    {
      method: "POST",
      body: JSON.stringify({ message }),
    },
  );
  const data = await parseApiResponse<{ request: AdminAccessRequest }>(response);
  return data.request;
}

export async function getMyAccessRequests(): Promise<AdminAccessRequest[]> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/admin/access-requests/me`);
  const data = await parseApiResponse<{ requests: AdminAccessRequest[] }>(response);
  return data.requests;
}

export async function createAccessRequest(
  eventId: number,
  message?: string,
): Promise<AdminAccessRequest> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/admin/access-requests`, {
    method: "POST",
    body: JSON.stringify({ eventId, message }),
  });
  const data = await parseApiResponse<{ request: AdminAccessRequest }>(response);
  return data.request;
}

export async function listAccessRequests(
  status?: string,
): Promise<AdminAccessRequest[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/access-requests${qs}`,
  );
  const data = await parseApiResponse<{ requests: AdminAccessRequest[] }>(
    response,
  );
  return data.requests;
}

export async function approveAccessRequest(
  id: number,
  options?: {
    reviewNote?: string;
    permissionLevel?: EventAdminPermissionLevel;
  },
): Promise<void> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/access-requests/${id}/approve`,
    {
      method: "POST",
      body: JSON.stringify({
        reviewNote: options?.reviewNote,
        permissionLevel: options?.permissionLevel ?? "registrations",
      }),
    },
  );
  await parseApiResponse(response);
}

export async function rejectAccessRequest(
  id: number,
  reviewNote?: string,
): Promise<void> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/access-requests/${id}/reject`,
    { method: "POST", body: JSON.stringify({ reviewNote }) },
  );
  await parseApiResponse(response);
}

export async function listEventAdmins(eventId: number): Promise<EventAdminUser[]> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/events/${eventId}/admins`,
  );
  const data = await parseApiResponse<{ admins: EventAdminUser[] }>(response);
  return data.admins;
}

export async function listAllEventAdmins(): Promise<EventAdminDirectoryUser[]> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/admin/event-admins`);
  const data = await parseApiResponse<{ admins: EventAdminDirectoryUser[] }>(
    response,
  );
  return data.admins;
}

export async function searchAdminUsers(
  search: string,
): Promise<AdminUserSearchResult[]> {
  const params = new URLSearchParams({ search });
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/users?${params}`,
  );
  const data = await parseApiResponse<{ users: AdminUserSearchResult[] }>(
    response,
  );
  return data.users;
}

export async function grantEventAdmin(
  eventId: number,
  userId: number,
  permissionLevel: EventAdminPermissionLevel = "registrations",
): Promise<void> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/events/${eventId}/admins`,
    {
      method: "POST",
      body: JSON.stringify({ userId, permissionLevel }),
    },
  );
  await parseApiResponse(response);
}

export async function updateEventAdminPermission(
  eventId: number,
  userId: number,
  permissionLevel: EventAdminPermissionLevel,
): Promise<void> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/events/${eventId}/admins/${userId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ permissionLevel }),
    },
  );
  await parseApiResponse(response);
}

export async function revokeEventAdmin(
  eventId: number,
  userId: number,
): Promise<void> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/events/${eventId}/admins/${userId}`,
    { method: "DELETE" },
  );
  await parseApiResponse(response);
}

export function hasFullEventAdminAccess(
  events: MyAdminEvent[],
  canPostEvents = false,
): boolean {
  return (
    canPostEvents ||
    events.some((e) => e.isSuperAdmin || e.permissionLevel === "full")
  );
}

export async function getEventDashboard(
  eventId: number,
): Promise<EventDashboard> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/events/${eventId}/dashboard`,
  );
  const data = await parseApiResponse<{ dashboard: EventDashboard }>(response);
  return data.dashboard;
}

export async function listEventRegistrations(
  eventId: number,
  page = 1,
  search?: string,
): Promise<{
  total: number;
  page: number;
  limit: number;
  registrations: EventRegistrationRow[];
}> {
  const params = new URLSearchParams({ page: String(page), limit: "50" });
  if (search) params.set("search", search);
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/events/${eventId}/registrations?${params}`,
  );
  return parseApiResponse(response);
}

export async function downloadEventRegistrationsExport(
  eventId: number,
  format: "csv" | "xlsx" = "csv",
): Promise<Blob> {
  const token = getAuthToken();
  const response = await fetch(
    `${API_ENDPOINT}/admin/events/${eventId}/registrations/export?format=${format}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!response.ok) throw new Error("Failed to download export");
  return response.blob();
}

export async function listEventRefundRequests(
  eventId: number,
  status = "processed",
): Promise<RefundRequestRow[]> {
  const qs = `?status=${encodeURIComponent(status)}`;
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/admin/events/${eventId}/refund-requests${qs}`,
  );
  const data = await parseApiResponse<{ requests: RefundRequestRow[] }>(response);
  return data.requests;
}

export function isSuperAdminRole(role?: string | null): boolean {
  return role === "super_admin" || role === "admin";
}
